import { useMemo } from 'react'
import { useData } from '../api'
import { useWidgetContext } from '../providers'

type MonthData = {
  period: string // ISO date string representing the month, e.g. '2025-01-01'
  data: Record<string, Record<string, number | null>> // component -> statistic -> value
}

export function useFullYearData(
  year: number,
  subject_type: string,
  subject_id: number,
  components: string[],
  statistics: string[]
): MonthData[] {
  const ctx = useWidgetContext()

  // Generate 12 months for the given year
  const periods = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = (i + 1).toString().padStart(2, '0')
      return `${year}-${month}-01`
    })
  }, [year])

  // Get metric from context
  const metricKey = ctx.metric || ''

  const { data } = useData({
    period_type: 'month',
    period_range: false,
    periods,
    subject_type,
    subject_ids: [subject_id],
    metrics: Array(components.length).fill(metricKey),
    components,
    statistics,
    competition_id: ctx.competition_id,
  })

  // Structure data as MonthData[]
  const monthData = useMemo(() => {
    // Initialize 12 months with empty data structures
    const result: MonthData[] = periods.map((period) => ({
      period,
      data: {},
    }))

    // Initialize component -> statistic structure for each month
    result.forEach((month) => {
      components.forEach((component) => {
        month.data[component] = {}
        statistics.forEach((statistic) => {
          month.data[component][statistic] = null
        })
      })
    })

    // Fill with actual data
    data.forEach((item) => {
      if (item.period && item.component && item.statistic) {
        const monthIndex = periods.indexOf(item.period)
        if (monthIndex !== -1) {
          result[monthIndex].data[item.component][item.statistic] = item.value
        }
      }
    })

    return result
  }, [data, periods, components, statistics])

  return monthData
}
